import {skipToken} from '@reduxjs/toolkit/query';
import {DataStatus} from 'platform/components';

import {useParams} from 'react-router-dom';

import {isNil, isNotNil} from 'ramda';

import {
  useGetRecordAuditDefinitionQuery,
  useUpdateRecordAuditDefinitionMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {RecordAuditDefinitionForm} from '../../components/RecordAuditDefinitionForm';
import {toRecordAuditDefinitionFormValue} from '../../utils/toRecordAuditDefinitionFormValue';
import {toUpdateRecordAuditDefinitionBody} from '../../utils/toRecordAuditDefinitionRequestBody';

export function Detail() {
  const {id: definitionId} = useParams<{id: string}>();
  const navigate = useNavigate();

  const recordAuditDefinitionDetailQuery = useGetRecordAuditDefinitionQuery(
    isNil(definitionId) ? skipToken : {definitionId}
  );

  const [updateRecordAuditDefinition, updateRecordAuditDefinitionQuery] =
    useUpdateRecordAuditDefinitionMutation();

  if (isNil(definitionId)) {
    return null;
  }

  return (
    <DataStatus
      isLoading={recordAuditDefinitionDetailQuery.isLoading}
      isError={recordAuditDefinitionDetailQuery.isError}
    >
      <SettingsTemplate
        header={{
          title: recordAuditDefinitionDetailQuery.data?.name || '',
          breadcrumbs: [
            {
              label: i18n.t('page.generalSettings.labels.title'),
              isCurrentPage: true,
            },
            {
              label: i18n.t('page.settings.generalChecklist.title'),
              href: settingsRoutes.recordAuditDefinition,
            },
          ],
        }}
        data-testid={testIds.settings.recordAuditDefinitionDetail()}
      >
        {isNotNil(recordAuditDefinitionDetailQuery.data) ? (
          <RecordAuditDefinitionForm
            key={recordAuditDefinitionDetailQuery.data.id}
            defaultValues={toRecordAuditDefinitionFormValue(recordAuditDefinitionDetailQuery.data)}
            onSubmit={async (formData, _setErrors, _reset, blockerContext) => {
              await updateRecordAuditDefinition({
                definitionId,
                body: toUpdateRecordAuditDefinitionBody(formData),
              })
                .unwrap()
                .then(() => {
                  if (blockerContext?.wasSubmittedFromBlocker) {
                    return;
                  }
                  navigate(settingsRoutes.recordAuditDefinition);
                })
                .catch((e: ApiError) =>
                  handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
                );
            }}
            isLoading={updateRecordAuditDefinitionQuery.isLoading}
            isEditing
          />
        ) : null}
      </SettingsTemplate>
    </DataStatus>
  );
}
